<template>
  <div class="overlay-customizer">
    <b-form>
      <!-- Text Input -->
      <b-form-group label="Overlay Text" label-class="font-weight-bold">
        <div class="d-flex">
          <b-form-input
              v-model="localSettings.text"
              placeholder="Enter text for overlay (use [FirstName] for personalization)"
              class="mb-1"
              @input="updateOverlaySettings"
          ></b-form-input>
          <b-btn class="ml-2 btn-outline-dark btn-link btn" style="width:150px" @click="insertName">Insert Name</b-btn>
        </div>
        <small class="text-muted">Use [FirstName] to add the recipient's name</small>
        <div class="d-flex align-items-center mt-2">
          <label class="text-nowrap mr-2">First Name Preview</label>
          <b-form-input v-model="localRecipientName" type="text" class="w-25" @input="updateName" />
        </div>
      </b-form-group>

      <!-- Font Size with numeric input and slider -->
      <b-form-group label="Font Size" label-class="font-weight-bold">
        <div class="d-flex align-items-center font-size-control">
          <b-form-input
            v-model.number="localSettings.fontSize"
            type="range"
            min="12"
            max="72"
            class="flex-grow-1"
            @input="updateOverlaySettings"
          ></b-form-input>
          <span class="ml-2 font-size-unit">{{localSettings.fontSize}}px</span>
        </div>
      </b-form-group>

      <!-- Style selector with custom buttons -->
      <b-form-group label="Overlay Style" label-class="font-weight-bold">
        <div class="style-selector">
          <b-button-group class="w-100 mb-2">
            <b-button
              v-for="style in styleOptions"
              :key="style.value"
              :pressed="localSettings.style === style.value"
              :variant="localSettings.style === style.value ? 'primary' : 'outline-primary'"
              class="style-button"
              @click="setStyle(style.value)"
            >
              {{ style.text }}
            </b-button>
          </b-button-group>
        </div>
      </b-form-group>

      <!-- Brand Color picker when branded style is selected -->
      <b-form-group v-if="localSettings.style === 'branded'" label="Brand Color" label-class="control-label-bold">
        <div class="color-picker-container d-flex">
          <b-form-input
            v-model="localSettings.brandColor"
            type="color"
            class="color-picker"
            @input="updateOverlaySettings"
          ></b-form-input>
          <b-form-input
            v-model="localSettings.brandColor"
            type="text"
            class="color-code ml-2"
            @input="updateOverlaySettings"
          ></b-form-input>
        </div>
      </b-form-group>

      <!-- Shape selector with visual buttons -->
      <b-form-group label="Shape:" label-class="control-label-bold">
        <div class="shape-selector">
          <div class="btn-group-wrap">
            <b-button-group>
              <b-button
                v-for="shape in shapeOptionsRow1"
                :key="shape.value"
                :pressed="localSettings.shape === shape.value"
                :variant="localSettings.shape === shape.value ? 'primary' : 'outline-primary'"
                class="shape-button"
                @click="setShape(shape.value)"
              >
                <i :class="getShapeIcon(shape.value)" class="shape-icon"></i>
                <span class="shape-label">{{ shape.text }}</span>
              </b-button>
            </b-button-group>
          </div>
          <div class="btn-group-wrap mt-2">
            <b-button-group>
              <b-button
                v-for="shape in shapeOptionsRow2"
                :key="shape.value"
                :pressed="localSettings.shape === shape.value"
                :variant="localSettings.shape === shape.value ? 'primary' : 'outline-primary'"
                class="shape-button"
                @click="setShape(shape.value)"
              >
                <i :class="getShapeIcon(shape.value)" class="shape-icon"></i>
                <span class="shape-label">{{ shape.text }}</span>
              </b-button>
            </b-button-group>
          </div>
        </div>
      </b-form-group>

      <!-- Rotation with slider and numeric input -->
      <b-form-group label="Rotation Angle" label-class="font-weight-bold">
        <div class="d-flex align-items-center rotation-control">
          <b-form-input
            v-model.number="localSettings.rotation"
            type="range"
            min="-30"
            max="30"
            class="flex-grow-1"
            @input="updateOverlaySettings"
          ></b-form-input>
          <span class="ml-2 rotation-unit">{{localSettings.rotation}}°</span>
        </div>
      </b-form-group>

      <!-- Animation Section -->
<!--      <hr class="my-3">
      <b-form-group class="mb-0">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label class="control-label-bold mb-0">Animation:</label>
          <b-form-checkbox
            v-model="localSettings.animation.enabled"
            switch
            @input="updateAnimationEnabled"
          >
            {{ localSettings.animation.enabled ? 'Enabled' : 'Disabled' }}
          </b-form-checkbox>
        </div>

        <template v-if="localSettings.animation.enabled">
          &lt;!&ndash; Animation Type &ndash;&gt;
          <div class="animation-type-container mb-3">
            <label class="d-block mb-2">Animation Type:</label>
            <b-button-group class="animation-buttons mb-2">
              <b-button
                v-for="type in animationTypes.slice(0, 3)"
                :key="type.value"
                :pressed="localSettings.animation.animationType === type.value"
                :variant="localSettings.animation.animationType === type.value ? 'primary' : 'outline-primary'"
                @click="setAnimationType(type.value)"
                size="sm"
                class="animation-button"
              >
                {{ type.text }}
              </b-button>
            </b-button-group>
            <b-button-group class="animation-buttons">
              <b-button
                v-for="type in animationTypes.slice(3)"
                :key="type.value"
                :pressed="localSettings.animation.animationType === type.value"
                :variant="localSettings.animation.animationType === type.value ? 'primary' : 'outline-primary'"
                @click="setAnimationType(type.value)"
                size="sm"
                class="animation-button"
              >
                {{ type.text }}
              </b-button>
            </b-button-group>
          </div>

          &lt;!&ndash; Animation Speed &ndash;&gt;
          <div class="animation-speed-container">
            <label class="d-block mb-2">Animation Speed:</label>
            <b-button-group class="w-100">
              <b-button
                v-for="speed in animationSpeeds"
                :key="speed.value"
                :pressed="localSettings.animation.speed === speed.value"
                :variant="localSettings.animation.speed === speed.value ? 'primary' : 'outline-primary'"
                @click="setAnimationSpeed(speed.value)"
                class="animation-speed-button"
              >
                {{ speed.text }}
              </b-button>
            </b-button-group>
          </div>
        </template>
      </b-form-group>-->
    </b-form>
  </div>
</template>

<script>
import {mapMutations} from "vuex";

export default {
  name: 'SimpleOverlayCustomizer',
  props: {
    settings: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      localRecipientName:'',
      localSettings: JSON.parse(JSON.stringify(this.settings)), // Deep copy
      styleOptions: [
        { value: 'white', text: 'White' },
        { value: 'translucent', text: 'Translucent' },
        { value: 'branded', text: 'Branded' }
      ],
      shapeOptions: [
        { value: 'rectangle', text: 'Rectangle' },
        { value: 'circle', text: 'Circle' },
        { value: 'speech-left', text: 'Speech Left' },
        { value: 'speech-right', text: 'Speech Right' },
        { value: 'ribbon', text: 'Ribbon' },
        { value: 'hexagon', text: 'Hexagon' }
      ],
      animationTypes: [
        { value: 'bounce', text: 'Bounce' },
        { value: 'fade', text: 'Fade' },
        { value: 'rotate', text: 'Rotate' },
        { value: 'pulse', text: 'Pulse' },
        { value: 'typewriter', text: 'Typewriter' }
      ],
      animationSpeeds: [
        { value: 'slow', text: 'Slow' },
        { value: 'medium', text: 'Medium' },
        { value: 'fast', text: 'Fast' }
      ]
    };
  },
  computed: {
    shapeOptionsRow1() {
      return this.shapeOptions.slice(0, 3);
    },
    shapeOptionsRow2() {
      return this.shapeOptions.slice(3);
    }
  },
  watch: {
    settings: {
      handler(newVal) {
        this.localSettings = JSON.parse(JSON.stringify(newVal)); // Deep copy
      },
      deep: true
    }
  },
  methods: {
    ...mapMutations('imageEditor',[
      'SET_RECIPIENT_NAME',
    ]),
    updateName(){
      this.SET_RECIPIENT_NAME(this.localRecipientName);
    },
    insertName(){
      this.localSettings.text += ' [FirstName]';
      this.updateOverlaySettings();
    },
    updateOverlaySettings() {
      this.$emit('settings-change', JSON.parse(JSON.stringify(this.localSettings)));
    },
    setStyle(style) {
      this.localSettings.style = style;
      this.updateOverlaySettings();
    },
    setShape(shape) {
      this.localSettings.shape = shape;
      this.updateOverlaySettings();
    },
    getShapeIcon(shape) {
      switch(shape) {
        case 'rectangle': return 'fas fa-square';
        case 'circle': return 'fas fa-circle';
        case 'speech-left': return 'fas fa-comment-alt';
        case 'speech-right': return 'fas fa-comment';
        case 'ribbon': return 'fas fa-bookmark';
        case 'hexagon': return 'fas fa-hexagon';
        default: return 'fas fa-square';
      }
    },
    updateAnimationEnabled(enabled) {
      this.localSettings.animation.enabled = enabled;
      this.updateOverlaySettings();
    },
    setAnimationType(type) {
      this.localSettings.animation.animationType = type;
      this.updateOverlaySettings();
    },
    setAnimationSpeed(speed) {
      this.localSettings.animation.speed = speed;
      this.updateOverlaySettings();
    }
  }
};
</script>

<style scoped>
.overlay-customizer {

}

.control-label-bold {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.3rem;
}

.font-size-control, .rotation-control {
  display: flex;
  align-items: center;
}

.font-size-number, .rotation-number {
  width: 60px;
  flex-shrink: 0;
}

.font-size-unit, .rotation-unit {
  width: 40px;
  flex-shrink: 0;
  text-align: right;
}

.style-selector .btn, .shape-selector .btn {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.shape-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5rem 0.25rem;
  flex: 1;
}

.shape-icon {
  font-size: 1.25rem;
  margin-bottom: 0.25rem;
}

.shape-label {
  font-size: 0.7rem;
  white-space: nowrap;
}

.color-picker-container {
  display: flex;
}

.color-picker {
  width: 50px;
  padding: 0;
  height: 38px;
}

.color-code {
  flex-grow: 1;
}

.btn-group-wrap {
  display: flex;
}

.btn-group-wrap .btn-group {
  flex-grow: 1;
  display: flex;
}

.animation-buttons {
  display: flex;
  width: 100%;
}

.animation-button, .animation-speed-button {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
